import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { ModalSettingsShape } from '../Modal/Modal';

export type GenericModalProps = {
  settings: ModalSettingsShape;
  children: React.ReactNode;
};

export function GenericModal({ settings, children }: GenericModalProps) {
  const {
    handleClose,
    title,
    headerBackground,
    childrenClassName,
    handleBackPress,
  } = settings;

  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll to top when children change
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [children]);

  return (
    <>
      {settings.title ? (
        <header
          className={classNames(
            'flex w-full items-center justify-between gap-3 rounded-t border-b p-5',
            { 'bg-gray-50': headerBackground }
          )}
        >
          <p
            className={classNames(
              'font-caudex inline-flex min-w-0 items-center gap-1 text-xl font-medium text-gray-900',
              { 'cursor-pointer': !!handleBackPress }
            )}
            onClick={handleBackPress}
          >
            {handleBackPress && (
              <ChevronLeftIcon className="h-6 w-6 flex-shrink-0 text-gray-900" />
            )}
            <span className="truncate">{title}</span>
          </p>

          <button
            aria-label="Close modal"
            className="ml-auto"
            onClick={handleBackPress ? handleBackPress : handleClose}
          >
            <XMarkIcon
              className={classNames(
                'h-6 w-6 text-gray-400 hover:text-gray-600',
                { 'text-gray-900': headerBackground }
              )}
            />
            <span className="sr-only">Close modal</span>
          </button>
        </header>
      ) : (
        <button onClick={handleClose} className="absolute top-4 right-4">
          <XMarkIcon
            className={classNames('h-6 w-6 text-gray-400 hover:text-gray-600', {
              'text-gray-900': headerBackground,
            })}
          />
          <span className="sr-only">Close modal</span>
        </button>
      )}

      <div
        ref={contentRef}
        className={classNames(
          'mb-1 h-full pb-5',
          // Only add 'overflow-y-auto' if no explicit overflow class is provided
          // Eg: `ModalNewRegistration.tsx` have `getChildrenClassName` to prevent clipping of dropdowns/popovers.
          !(childrenClassName || '').includes('overflow-') && 'overflow-y-auto',
          childrenClassName
        )}
      >
        {children}
      </div>
    </>
  );
}

export default GenericModal;
